import React from "react";
import {
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { MoneyField } from "../../fields/MoneyField";
import { SectionAccordion } from "./SectionAccordion";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import {
  ContractSectionProps,
  ChangeCountContractSectionProps,
} from "../../../../types";

export interface ContractSectionComponentProps {
  state: ContractSectionProps;
  setState: (state: ContractSectionProps) => void;
  formErrors: Record<string, string>;
  updateField: (field: string, value: string) => void;
  total: number;
  fullRetail: number;
  mode: "edit" | "review";
  /** "create" = 2-col layout (xs 9/3), "edit" / "view" = 3-col layout (xs 6/3/3) */
  pageVariant: "create" | "edit" | "view";
  /** Required for edit/view variant */
  initialState?: ContractSectionProps;
  changeCount?: ChangeCountContractSectionProps;
  forceMobile?: boolean;
  /** Spec-only summary rows */
  paType?: "spec" | "sold";
  discountToBP?: number;
  bpPrice?: number;
  discountToBL?: number;
  blPrice?: number;
}

export function ContractSection({
  state,
  setState,
  formErrors,
  updateField,
  total,
  fullRetail,
  mode,
  pageVariant,
  initialState,
  changeCount,
  forceMobile,
  paType,
  discountToBP,
  bpPrice,
  discountToBL,
  blPrice,
}: ContractSectionComponentProps) {
  const isMobile = forceMobile === true;
  const isEditView = pageVariant !== "create";
  const isView = pageVariant === "view";
  const viewFieldSx = {
    "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
    "& .MuiOutlinedInput-input.Mui-disabled": { WebkitTextFillColor: "rgba(0,0,0,0.45)" },
    "& .MuiInputAdornment-root .MuiTypography-root": { color: "rgba(0,0,0,0.45)" },
  };
  const labelXs = isMobile ? 12 : (isEditView ? 6 : 9);
  const fieldXs = isMobile ? 12 : 3;

  const dollarAdornment = <InputAdornment position="start" sx={{ ml: 1 }}>$</InputAdornment>;
  const negDollarAdornment = <InputAdornment position="start">-$</InputAdornment>;

  function initialText(val: number | undefined): string {
    return val === undefined ? "" : val.toString().replace(/^0+/, "");
  }
  function changeCountText(count: number): string {
    return count > 1 ? ` (${count})` : "";
  }

  /** Disabled outlined field for view mode */
  function ViewField({ value, adornment = dollarAdornment }: { value: number | undefined; adornment?: React.ReactNode }) {
    return (
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        disabled
        value={value ?? ""}
        InputProps={{ startAdornment: adornment }}
        sx={viewFieldSx}
      />
    );
  }

  return (
    <SectionAccordion
      id="contract"
      title="Contract"
      icon={<TextSnippetIcon fontSize="large" sx={{ color: "#ACACAC" }} />}
      total={total}
      forceMobile={forceMobile}
    >
      {isEditView && !isMobile && (
        <>
          <Grid item xs={6} display="flex">
            <Typography variant="body2" fontWeight={600} sx={{ alignSelf: "center" }} />
          </Grid>
          <Grid item xs={fieldXs} display="flex">
            <Typography variant="body2" color="text.secondary" fontWeight={600} bgcolor="lightgray" sx={{ alignSelf: "center" }}>
              Initial Values<br />(Change Count, if &gt; 1)
            </Typography>
          </Grid>
          <Grid item xs={fieldXs}>
            <Typography variant="body2" color="text.secondary" fontWeight={600} bgcolor="lightgray" sx={{ alignSelf: "center" }}>
              Current Values
            </Typography>
          </Grid>
        </>
      )}

      {/* Brochure Price */}
      <Grid item xs={labelXs} display="flex">
        <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
          Brochure Price*
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {initialText(initialState?.BrochurePrice)}
            {changeCountText(changeCount?.BrochurePrice ?? 0)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {isView ? (
          <ViewField value={state.BrochurePrice} />
        ) : isEditView ? (
          <TextField
            inputProps={{ inputMode: "numeric", min: 0, max: 9999999.9999 }}
            type="text"
            disabled={mode === "review"}
            value={state.BrochurePrice === undefined ? "" : state.BrochurePrice.toString().replace(/^0+/, "")}
            onChange={(e) => updateField("BrochurePrice", e.target.value)}
            error={formErrors["BrochurePrice"] !== ""}
            helperText={formErrors["BrochurePrice"]}
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{ startAdornment: dollarAdornment }}
          />
        ) : (
          <MoneyField
            label="Brochure Price*"
            value={state.BrochurePrice ?? null}
            onChange={(v) => updateField("BrochurePrice", v === null ? "" : String(v))}
            disabled={mode === "review"}
            allowNegative={false}
            min={0}
            max={9999999.9999}
            error={formErrors["BrochurePrice"] !== ""}
            helperText={formErrors["BrochurePrice"]}
          />
        )}
      </Grid>

      {/* Lot Premium Price */}
      <Grid item xs={labelXs} display="flex">
        <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
          Lot Premium Price
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {initialState?.LotPremium === undefined ? "" : initialState.LotPremium.toString()}
            {changeCountText(changeCount?.LotPremium ?? 0)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {isView ? (
          <ViewField value={state.LotPremium} />
        ) : isEditView ? (
          <TextField
            disabled={mode === "review"}
            type="text"
            value={state.LotPremium === undefined ? "" : state.LotPremium.toString()}
            onChange={(e) => updateField("LotPremium", e.target.value)}
            error={formErrors["LotPremium"] !== ""}
            helperText={formErrors["LotPremium"]}
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{ startAdornment: dollarAdornment }}
          />
        ) : (
          <MoneyField
            label="Lot Premium Price"
            value={state.LotPremium ?? null}
            onChange={(v) => updateField("LotPremium", v === null ? "" : String(v))}
            disabled={mode === "review"}
            allowNegative={true}
            min={-9999999.9999}
            max={9999999.9999}
            error={formErrors["LotPremium"] !== ""}
            helperText={formErrors["LotPremium"]}
          />
        )}
      </Grid>

      {/* Optional Architectural Plan Changes */}
      <Grid item xs={labelXs} display="flex">
        <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
          Optional Architectural Plan Changes
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {initialText(initialState?.OptionalArchitecturalPlanChanges)}
            {changeCountText(changeCount?.OptionalArchitecturalPlanChanges ?? 0)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {isView ? (
          <ViewField value={state.OptionalArchitecturalPlanChanges} />
        ) : isEditView ? (
          <TextField
            disabled={mode === "review"}
            type="text"
            inputProps={{ inputMode: "numeric", min: 0 }}
            value={state.OptionalArchitecturalPlanChanges === undefined ? "" : state.OptionalArchitecturalPlanChanges.toString().replace(/^0+/, "")}
            onChange={(e) => updateField("OptionalArchitecturalPlanChanges", e.target.value)}
            error={formErrors["OptionalArchitecturalPlanChanges"] !== ""}
            helperText={formErrors["OptionalArchitecturalPlanChanges"]}
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{ startAdornment: dollarAdornment }}
          />
        ) : (
          <MoneyField
            label="Optional Architectural Plan Changes"
            value={state.OptionalArchitecturalPlanChanges ?? null}
            onChange={(v) => updateField("OptionalArchitecturalPlanChanges", v === null ? "" : String(v))}
            disabled={mode === "review"}
            allowNegative={false}
            min={0}
            max={9999999.9999}
            error={formErrors["OptionalArchitecturalPlanChanges"] !== ""}
            helperText={formErrors["OptionalArchitecturalPlanChanges"]}
          />
        )}
      </Grid>

      {/* Buyer Selected Options/Spec Options */}
      <Grid item xs={labelXs} display="flex">
        <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
          Buyer Selected Options/Spec Options
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {initialText(initialState?.BuyerSelectedOptions)}
            {changeCountText(changeCount?.BuyerSelectedOptions ?? 0)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {isView ? (
          <ViewField value={state.BuyerSelectedOptions} />
        ) : isEditView ? (
          <TextField
            disabled={mode === "review"}
            type="text"
            inputProps={{ inputMode: "numeric", min: 0 }}
            value={state.BuyerSelectedOptions === undefined ? "" : state.BuyerSelectedOptions.toString().replace(/^0+/, "")}
            onChange={(e) => updateField("BuyerSelectedOptions", e.target.value)}
            error={formErrors["BuyerSelectedOptions"] !== ""}
            helperText={formErrors["BuyerSelectedOptions"]}
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{ startAdornment: dollarAdornment }}
          />
        ) : (
          <MoneyField
            label="Buyer Selected Options/Spec Options"
            value={state.BuyerSelectedOptions ?? null}
            onChange={(v) => updateField("BuyerSelectedOptions", v === null ? "" : String(v))}
            disabled={mode === "review"}
            allowNegative={false}
            min={0}
            max={9999999.9999}
            error={formErrors["BuyerSelectedOptions"] !== ""}
            helperText={formErrors["BuyerSelectedOptions"]}
          />
        )}
      </Grid>

      {/* Sold-only fields */}
      {paType === "sold" && (
        <>
          {/* Custom Architectural Plan Changes */}
          <Grid item xs={labelXs} display="flex">
            <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
              Custom Architectural Plan Changes
            </Typography>
          </Grid>
          {isEditView && !isMobile && (
            <Grid item xs={fieldXs}>
              <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
                {initialText(initialState?.CustomArchitecturalPlanChanges)}
                {changeCountText(changeCount?.CustomArchitecturalPlanChanges ?? 0)}
              </Typography>
            </Grid>
          )}
          <Grid item xs={fieldXs}>
            {isView ? (
              <ViewField value={state.CustomArchitecturalPlanChanges} />
            ) : isEditView ? (
              <TextField
                disabled={mode === "review"}
                type="text"
                inputProps={{ inputMode: "numeric", min: 0 }}
                value={state.CustomArchitecturalPlanChanges === undefined ? "" : state.CustomArchitecturalPlanChanges.toString().replace(/^0+/, "")}
                onChange={(e) => updateField("CustomArchitecturalPlanChanges", e.target.value)}
                error={formErrors["CustomArchitecturalPlanChanges"] !== ""}
                helperText={formErrors["CustomArchitecturalPlanChanges"]}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{ startAdornment: dollarAdornment }}
              />
            ) : (
              <MoneyField
                label="Custom Architectural Plan Changes"
                value={state.CustomArchitecturalPlanChanges ?? null}
                onChange={(v) => updateField("CustomArchitecturalPlanChanges", v === null ? "" : String(v))}
                disabled={mode === "review"}
                allowNegative={false}
                min={0}
                max={9999999.9999}
                error={formErrors["CustomArchitecturalPlanChanges"] !== ""}
                helperText={formErrors["CustomArchitecturalPlanChanges"]}
              />
            )}
          </Grid>

          {/* Options Given TBD/Seller Paid Closing Costs */}
          <Grid item xs={labelXs} display="flex">
            <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
              Options Given TBD/Seller Paid Closing Costs
            </Typography>
          </Grid>
          {isEditView && !isMobile && (
            <Grid item xs={fieldXs}>
              <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
                {initialText(initialState?.OptionsGiven)}
                {changeCountText(changeCount?.OptionsGiven ?? 0)}
              </Typography>
            </Grid>
          )}
          <Grid item xs={fieldXs}>
            {isView ? (
              <ViewField value={state.OptionsGiven} />
            ) : isEditView ? (
              <TextField
                disabled={mode === "review"}
                type="text"
                inputProps={{ inputMode: "numeric", min: 0 }}
                value={state.OptionsGiven === undefined ? "" : state.OptionsGiven.toString().replace(/^0+/, "")}
                onChange={(e) => updateField("OptionsGiven", e.target.value)}
                error={formErrors["OptionsGiven"] !== ""}
                helperText={formErrors["OptionsGiven"]}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{ startAdornment: dollarAdornment }}
              />
            ) : (
              <MoneyField
                label="Options Given TBD/Seller Paid Closing Costs"
                value={state.OptionsGiven ?? null}
                onChange={(v) => updateField("OptionsGiven", v === null ? "" : String(v))}
                disabled={mode === "review"}
                allowNegative={false}
                min={0}
                max={9999999.9999}
                error={formErrors["OptionsGiven"] !== ""}
                helperText={formErrors["OptionsGiven"]}
              />
            )}
          </Grid>

          {/* Options Retail Value */}
          <Grid item xs={labelXs} display="flex">
            <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
              Options Retail Value @ N/C - (Incl Spec Opts)
            </Typography>
          </Grid>
          {isEditView && !isMobile && (
            <Grid item xs={fieldXs}>
              <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
                {initialText(initialState?.OptionsRetailValue)}
                {changeCountText(changeCount?.OptionsRetailValue ?? 0)}
              </Typography>
            </Grid>
          )}
          <Grid item xs={fieldXs}>
            {isView ? (
              <ViewField value={state.OptionsRetailValue} />
            ) : isEditView ? (
              <TextField
                disabled={mode === "review"}
                type="text"
                inputProps={{ inputMode: "numeric", min: 0 }}
                value={state.OptionsRetailValue === undefined ? "" : state.OptionsRetailValue.toString().replace(/^0+/, "")}
                onChange={(e) => updateField("OptionsRetailValue", e.target.value)}
                error={formErrors["OptionsRetailValue"] !== ""}
                helperText={formErrors["OptionsRetailValue"]}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{ startAdornment: dollarAdornment }}
              />
            ) : (
              <MoneyField
                label="Options Retail Value @ N/C - (Incl Spec Opts)"
                value={state.OptionsRetailValue ?? null}
                onChange={(v) => updateField("OptionsRetailValue", v === null ? "" : String(v))}
                disabled={mode === "review"}
                allowNegative={false}
                min={0}
                max={9999999.9999}
                error={formErrors["OptionsRetailValue"] !== ""}
                helperText={formErrors["OptionsRetailValue"]}
              />
            )}
          </Grid>

          {/* Cash Options Paid */}
          <Grid item xs={labelXs} display="flex">
            <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
              Cash Options Paid
            </Typography>
          </Grid>
          {isEditView && !isMobile && (
            <Grid item xs={fieldXs}>
              <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
                {initialText(initialState?.CashOptionsPaid)}
                {changeCountText(changeCount?.CashOptionsPaid ?? 0)}
              </Typography>
            </Grid>
          )}
          <Grid item xs={fieldXs}>
            {isView ? (
              <ViewField value={state.CashOptionsPaid} />
            ) : isEditView ? (
              <TextField
                disabled={mode === "review"}
                type="text"
                inputProps={{ inputMode: "numeric", min: 0 }}
                value={state.CashOptionsPaid === undefined ? "" : state.CashOptionsPaid.toString().replace(/^0+/, "")}
                onChange={(e) => updateField("CashOptionsPaid", e.target.value)}
                error={formErrors["CashOptionsPaid"] !== ""}
                helperText={formErrors["CashOptionsPaid"]}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{ startAdornment: dollarAdornment }}
              />
            ) : (
              <MoneyField
                label="Cash Options Paid"
                value={state.CashOptionsPaid ?? null}
                onChange={(v) => updateField("CashOptionsPaid", v === null ? "" : String(v))}
                disabled={mode === "review"}
                allowNegative={false}
                min={0}
                max={9999999.9999}
                error={formErrors["CashOptionsPaid"] !== ""}
                helperText={formErrors["CashOptionsPaid"]}
              />
            )}
          </Grid>

          {/* Sales Allowance Options */}
          <Grid item xs={labelXs} display="flex">
            <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
              Sales Allowance Options
            </Typography>
          </Grid>
          {isEditView && !isMobile && (
            <Grid item xs={fieldXs}>
              <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
                {initialText(initialState?.SalesAllowanceOptions)}
                {changeCountText(changeCount?.SalesAllowanceOptions ?? 0)}
              </Typography>
            </Grid>
          )}
          <Grid item xs={fieldXs}>
            {isView ? (
              <ViewField value={state.SalesAllowanceOptions} adornment={negDollarAdornment} />
            ) : isEditView ? (
              <TextField
                disabled={mode === "review"}
                type="text"
                inputProps={{ inputMode: "numeric", min: 0 }}
                value={state.SalesAllowanceOptions === undefined ? "" : state.SalesAllowanceOptions.toString().replace(/^0+/, "")}
                onChange={(e) => updateField("SalesAllowanceOptions", e.target.value)}
                error={formErrors["SalesAllowanceOptions"] !== ""}
                helperText={formErrors["SalesAllowanceOptions"]}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{ startAdornment: negDollarAdornment }}
              />
            ) : (
              <MoneyField
                label="Sales Allowance Options"
                value={state.SalesAllowanceOptions ?? null}
                onChange={(v) => updateField("SalesAllowanceOptions", v === null ? "" : String(v))}
                disabled={mode === "review"}
                allowNegative={false}
                min={0}
                max={9999999.9999}
                error={formErrors["SalesAllowanceOptions"] !== ""}
                helperText={formErrors["SalesAllowanceOptions"]}
              />
            )}
          </Grid>

          {/* Sales Allowance Closing Costs */}
          <Grid item xs={labelXs} display="flex">
            <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
              Sales Allowance Closing Costs
            </Typography>
          </Grid>
          {isEditView && !isMobile && (
            <Grid item xs={fieldXs}>
              <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
                {initialText(initialState?.SalesAllowanceClosingCosts)}
                {changeCountText(changeCount?.SalesAllowanceClosingCosts ?? 0)}
              </Typography>
            </Grid>
          )}
          <Grid item xs={fieldXs}>
            {isView ? (
              <ViewField value={state.SalesAllowanceClosingCosts} adornment={negDollarAdornment} />
            ) : isEditView ? (
              <TextField
                disabled={mode === "review"}
                type="text"
                inputProps={{ inputMode: "numeric", min: 0 }}
                value={state.SalesAllowanceClosingCosts === undefined ? "" : state.SalesAllowanceClosingCosts.toString().replace(/^0+/, "")}
                onChange={(e) => updateField("SalesAllowanceClosingCosts", e.target.value)}
                error={formErrors["SalesAllowanceClosingCosts"] !== ""}
                helperText={formErrors["SalesAllowanceClosingCosts"]}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{ startAdornment: negDollarAdornment }}
              />
            ) : (
              <MoneyField
                label="Sales Allowance Closing Costs"
                value={state.SalesAllowanceClosingCosts ?? null}
                onChange={(v) => updateField("SalesAllowanceClosingCosts", v === null ? "" : String(v))}
                disabled={mode === "review"}
                allowNegative={false}
                min={0}
                max={9999999.9999}
                error={formErrors["SalesAllowanceClosingCosts"] !== ""}
                helperText={formErrors["SalesAllowanceClosingCosts"]}
              />
            )}
          </Grid>

          {/* Sales Allowance Price Reduced */}
          <Grid item xs={labelXs} display="flex">
            <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
              Sales Allowance Price Reduced
            </Typography>
          </Grid>
          {isEditView && !isMobile && (
            <Grid item xs={fieldXs}>
              <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
                {initialText(initialState?.SalesAllowancePriceReduced)}
                {changeCountText(changeCount?.SalesAllowancePriceReduced ?? 0)}
              </Typography>
            </Grid>
          )}
          <Grid item xs={fieldXs}>
            {isView ? (
              <ViewField value={state.SalesAllowancePriceReduced} adornment={negDollarAdornment} />
            ) : isEditView ? (
              <TextField
                disabled={mode === "review"}
                type="text"
                inputProps={{ inputMode: "numeric", min: 0 }}
                value={state.SalesAllowancePriceReduced === undefined ? "" : state.SalesAllowancePriceReduced.toString().replace(/^0+/, "")}
                onChange={(e) => updateField("SalesAllowancePriceReduced", e.target.value)}
                error={formErrors["SalesAllowancePriceReduced"] !== ""}
                helperText={formErrors["SalesAllowancePriceReduced"]}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{ startAdornment: negDollarAdornment }}
              />
            ) : (
              <MoneyField
                label="Sales Allowance Price Reduced"
                value={state.SalesAllowancePriceReduced ?? null}
                onChange={(v) => updateField("SalesAllowancePriceReduced", v === null ? "" : String(v))}
                disabled={mode === "review"}
                allowNegative={false}
                min={0}
                max={9999999.9999}
                error={formErrors["SalesAllowancePriceReduced"] !== ""}
                helperText={formErrors["SalesAllowancePriceReduced"]}
              />
            )}
          </Grid>
        </>
      )}

      {paType === "sold" && (
        <>
          {/* Contract Price total */}
          <Grid item xs={isMobile ? 12 : 9} display="flex" justifyContent={isMobile ? "flex-start" : "flex-end"} mt={5}>
            <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
              Contract Price
            </Typography>
          </Grid>
          <Grid item xs={fieldXs} mt={isMobile ? 0 : 5}>
            <TextField
              type="number"
              inputProps={{ min: 0 }}
              disabled
              value={total}
              variant="filled"
              size="small"
              fullWidth
              InputProps={{ startAdornment: dollarAdornment }}
            />
          </Grid>
        </>
      )}

      {/* Spec-only summary rows */}
      {paType === "spec" && (
        <>
          {/* Full Retail */}
          <Grid item xs={isMobile ? 12 : 9} display="flex" justifyContent={isMobile ? "flex-start" : "flex-end"}>
            <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
              Full Retail
            </Typography>
          </Grid>
          <Grid item xs={fieldXs}>
            <TextField type="number" disabled value={fullRetail !== undefined ? Math.round(fullRetail) : ""} variant="filled" size="small" fullWidth InputProps={{ startAdornment: dollarAdornment }} />
          </Grid>

          {/* Discount to BP */}
          <Grid item xs={isMobile ? 12 : 9} display="flex" justifyContent={isMobile ? "flex-start" : "flex-end"}>
            <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>Discount to BP</Typography>
          </Grid>
          <Grid item xs={fieldXs}>
            <TextField type="number" disabled value={Math.round(discountToBP ?? 0)} variant="filled" size="small" fullWidth InputProps={{ startAdornment: dollarAdornment }} />
          </Grid>

          {/* BP Price */}
          <Grid item xs={isMobile ? 12 : 9} display="flex" justifyContent={isMobile ? "flex-start" : "flex-end"}>
            <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>BP Price</Typography>
          </Grid>
          <Grid item xs={fieldXs}>
            <TextField type="number" disabled value={Math.round(bpPrice ?? 0)} variant="filled" size="small" fullWidth InputProps={{ startAdornment: dollarAdornment }} />
          </Grid>

          {/* Discount to BL */}
          <Grid item xs={isMobile ? 12 : 9} display="flex" justifyContent={isMobile ? "flex-start" : "flex-end"}>
            <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>Discount to BL</Typography>
          </Grid>
          <Grid item xs={fieldXs}>
            <TextField type="number" disabled value={Math.round(discountToBL ?? 0)} variant="filled" size="small" fullWidth InputProps={{ startAdornment: dollarAdornment }} />
          </Grid>

          {/* BL Price */}
          <Grid item xs={isMobile ? 12 : 9} display="flex" justifyContent={isMobile ? "flex-start" : "flex-end"}>
            <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>BL Price</Typography>
          </Grid>
          <Grid item xs={fieldXs}>
            <TextField type="number" disabled value={Math.round(blPrice ?? 0)} variant="filled" size="small" fullWidth InputProps={{ startAdornment: dollarAdornment }} />
          </Grid>
        </>
      )}
    </SectionAccordion>
  );
}
